/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import { rgba } from 'polished';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronLeftIcon, ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { ITimeMachine, Tabs, tokens } from '@treams/treams-ui';

const Clip = styled(Box)<{ right?: boolean; wide?: boolean }>`
	position: absolute;
	top: 0;
	left: -16px;
	width: ${(props) => (props.wide ? '64' : '16')}px;
	bottom: 0;
	z-index: 2;
	background: linear-gradient(to right, ${tokens.surface.white}, ${rgba(tokens.surface.white, 0)});

	${(props) =>
		props.right &&
		`
        left: auto;
        bottom: 1px;
		right: 0;
        background: linear-gradient(
            to left,
            ${tokens.surface.white},
            ${rgba(tokens.surface.white, 0)}
        );
    `}
`;

const HiddenScrollbar = styled(Box)`
	scrollbar-width: thin;
	scrollbar-color: transparent transparent;

	overflow-x: auto;

	::-webkit-scrollbar {
		display: none;
	}
`;

const HorizontalRevealIcon = styled(Flex)<{ right?: boolean }>`
	position: absolute;
	${(props) => (props.right ? 'right: 8px;' : 'left: 0;')}
	border-radius: 17px;
	top: 50%;
	transform: translateY(-50%);
	z-index: 3;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	background-color: ${tokens.surface.secondary};

	svg path {
		fill: ${tokens.icon.secondary};
	}

	:hover {
		svg path {
			fill: ${tokens.brand.icon};
		}
	}
`;

export default function AnalyticsMetricDetailsTabs(props: {
	ids: string[];
	type: graphql.AnalyticsType;
	filters: graphql.AnalyticsFiltersV2;
	timeMachine: ITimeMachine;
	downloadButton?: ReactNode;
}) {
	const { t } = useTranslation();

	const {
		params: { groupID, metricID },
	} = useRouteMatch<any>('/analytics/metric/:groupID/:metricID')!;
	const query = new URL(window.location.href).search;

	const scrollRef = useRef<HTMLDivElement>();

	const [reveal, setReveal] = useState<'left' | 'right'>('right');
	const usesScroll = (scrollRef.current?.scrollWidth ?? 0) > (scrollRef.current?.clientWidth ?? 1);

	useEffect(() => {
		const onScroll = (event: WheelEvent) => {
			if ((event.currentTarget as any).scrollLeft < 10) {
				setReveal('right');
			} else {
				setReveal('left');
			}
		};
		scrollRef.current?.addEventListener('scroll', onScroll);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		return () => scrollRef.current?.removeEventListener('scroll', onScroll);
	});

	return (
		<Tabs>
			<Flex flexGrow={1} flexShrink={1}>
				<Box style={{ position: 'relative' }}>
					<Clip wide={reveal === 'left'} />
					{usesScroll &&
						(reveal === 'left' ? (
							<HorizontalRevealIcon
								size={34}
								onClick={() => {
									scrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
								}}
							>
								<Box size={28}>
									<ChevronLeftIcon />
								</Box>
							</HorizontalRevealIcon>
						) : (
							<HorizontalRevealIcon
								size={34}
								right
								onClick={() => {
									scrollRef.current?.scrollTo({ top: 0, left: 2000, behavior: 'smooth' });
								}}
							>
								<Box size={28}>
									<ChevronRightIcon />
								</Box>
							</HorizontalRevealIcon>
						))}
					<HiddenScrollbar
						ref={scrollRef as any}
						ml={-3}
						flexGrow={0}
						flexShrink={1}
						style={{ position: 'relative' }}
					>
						<Flex>
							{props.ids.map((id, index) => (
								<Tabs.Item
									key={id}
									title={t(`analytics.metric.details.${id}`)}
									path={
										index === 0
											? `/analytics/metric/${groupID}/${metricID}${query}`
											: `/analytics/metric/${groupID}/${metricID}/${_.kebabCase(id)}${query}`
									}
								/>
							))}
							<Box flexGrow={0} flexShrink={0} width={16} />
						</Flex>
					</HiddenScrollbar>
					<Clip right wide={reveal === 'right'} />
				</Box>
				<Box flexGrow={1} flexShrink={1} />
				{props.downloadButton && (
					<Flex alignItems="center" flexGrow={0} flexShrink={0}>
						{props.downloadButton}
					</Flex>
				)}
			</Flex>
		</Tabs>
	);
}
